import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerSection,
  DrawerDetailRow,
  DrawerFooter,
} from '@/components/ui/drawer'
import { getVersionById } from '@/api/versions.api'
import type { VersionItem } from '@/types/api'

interface VersionDetailDrawerProps {
  versionId: string | null
  onClose: () => void
  onUpdate?: (version: VersionItem) => void
  onDownload?: (url: string, fileName?: string) => void
}

export function VersionDetailDrawer({
  versionId,
  onClose,
  onUpdate,
  onDownload,
}: VersionDetailDrawerProps) {
  const { data: version, isLoading } = useQuery({
    queryKey: ['version-detail', versionId],
    queryFn: () =>
      versionId ? getVersionById(versionId) : Promise.resolve(null),
    enabled: !!versionId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  return (
    <Drawer
      open={!!versionId}
      onClose={onClose}
      title="CHI TIẾT PHIÊN BẢN"
      styles={{ body: { overflow: 'auto' } }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            className="min-w-[160px] h-10"
          >
            Đóng
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => version && onUpdate?.(version)}
            className="min-w-[160px] h-10"
            disabled={!version || isLoading}
          >
            Cập nhật
          </Button>
        </DrawerFooter>
      }
    >
      {isLoading && (
        <div className="p-6 text-center text-gray-500">
          Đang tải chi tiết...
        </div>
      )}

      {version && (
        <div>
          <DrawerSection>
            <DrawerDetailRow label="Tên phiên bản:" value={version.name} />
            <DrawerDetailRow
              label="Bản cài:"
              value={
                version.fileApkInfo ? (
                  <button
                    type="button"
                    className="cursor-pointer text-[#2196F3] underline hover:no-underline"
                    onClick={() => {
                      const url = version.fileApkInfo?.fileUrl
                      const name = version.fileApkInfo?.originFileName
                      if (url) onDownload?.(url, name)
                    }}
                  >
                    {version.fileApkInfo.originFileName}
                  </button>
                ) : (
                  '--'
                )
              }
            />
            <DrawerDetailRow
              label="Hướng dẫn sử dụng:"
              value={
                version.fileGuildInfo ? (
                  <button
                    type="button"
                    className="cursor-pointer text-[#2196F3] underline hover:no-underline"
                    onClick={() => {
                      const url = version.fileGuildInfo?.fileUrl
                      const name = version.fileGuildInfo?.originFileName
                      if (url) onDownload?.(url, name)
                    }}
                  >
                    {version.fileGuildInfo.originFileName}
                  </button>
                ) : (
                  '--'
                )
              }
            />
            <DrawerDetailRow
              label="Trạng thái:"
              value={
                <span
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                    version.status === 1
                      ? 'bg-[#EAF7ED] text-[#2FB14F]'
                      : 'bg-[#FDECEB] text-[#EE4036]'
                  }`}
                >
                  {version.status === 1 ? 'Hoạt động' : 'Không hoạt động'}
                </span>
              }
            />
            <DrawerDetailRow
              label="Ghi chú:"
              value={version.description || 'Không có ghi chú'}
              alignTop
            />
          </DrawerSection>
        </div>
      )}
    </Drawer>
  )
}
